import { apiFetch } from "@/lib/api";

export interface GosiListRow {
  mv_id: number;
  number: string;
  status: string | null;
  date_created: string | null;
  sales_order_id: number | null;
  sales_order_number: string | null;
  customer_purchase_order_number: string | null;
  client_name: string | null;
  end_user_name: string | null;
}

export interface GosiDetailLine {
  sku: string | null;
  description: string | null;
  quantity: number | null;
  unit_price: number | null;
}

export interface GosiDetailDocument {
  mv_id: number;
  number: string;
  status: string | null;
  terms: string | null;
  date_created: string | null;
  mv_updated: string | null;
  sales_order_id: number | null;
  sales_order_number: string | null;
  customer_purchase_order_number: string | null;
  client_name: string | null;
  end_user_name: string | null;
  commission_je_support_ticket_id: number | null;
  ship_to_recipient: string | null;
  ship_to_address_line_1: string | null;
  ship_to_address_line_2: string | null;
  ship_to_address_line_3: string | null;
  ship_to_city: string | null;
  ship_to_state: string | null;
  ship_to_zip: string | null;
  ship_to_country: string | null;
  bill_to_recipient: string | null;
  bill_to_address_line_1: string | null;
  bill_to_address_line_2: string | null;
  bill_to_address_line_3: string | null;
  bill_to_city: string | null;
  bill_to_state: string | null;
  bill_to_zip: string | null;
  bill_to_country: string | null;
  qb_id: string | null;
}

export interface GosiDetail {
  document: GosiDetailDocument;
  lines: GosiDetailLine[];
  sales_order: GosiSalesOrderSummary | null;
  sales_order_lines: GosiSalesOrderBatchLine[];
  serial_count: number;
}

export interface GosiSalesOrderSummary {
  sales_order_id: number;
  sales_order_number: string;
  customer_purchase_order_number: string | null;
  sgs_ship_by_date: string | null;
  client_name: string | null;
  order_total_quantity: number | null;
  total_shipped_quantity: number;
}

export interface GosiSalesOrderBatchLine {
  batch_item_id: number;
  batch_id: number;
  build_order_id: number | null;
  build_order_number: string | null;
  sku: string;
  batch_item_quantity: number;
  quantity_shipped: number;
  quantity_assembled: number;
  sgs_ship_by_date: string | null;
  shipment_id: number | null;
  tla_build_order_id: number | null;
  tla_build_order_number: string | null;
  tla_purchase_order_number: string | null;
}

export interface GosiSerialRow {
  serial_fci_id: number;
  device_label_serial: string | null;
  finished_sku: string | null;
  batch_line_sku: string | null;
  box_serial: string | null;
  imei: string | null;
  shipment_id: number | null;
  carton_number: number | null;
  wireless_portal_org_name: string | null;
  wireless_portal_group_names: string | null;
}

export async function fetchGosiInvoiceList(query: string, limit = 100): Promise<GosiListRow[]> {
  const data = await apiFetch<{ rows: GosiListRow[] }>("/api/shipping/gosi-details/invoices", {
    query: { q: query.trim(), limit: String(limit) },
  });
  return data.rows;
}

export async function fetchGosiDetail(mvDocumentId: number): Promise<GosiDetail> {
  const data = await apiFetch<{ gosi: GosiDetail }>(`/api/shipping/gosi-details/${String(mvDocumentId)}`);
  return data.gosi;
}

export async function fetchGosiSerials(mvDocumentId: number): Promise<GosiSerialRow[]> {
  const data = await apiFetch<{ rows: GosiSerialRow[] }>(
    `/api/shipping/gosi-details/${String(mvDocumentId)}/serials`,
  );
  return data.rows;
}

export async function fetchGosiSerialsPortal(
  mvDocumentId: number,
): Promise<
  Record<
    string,
    {
      wireless_portal_org_name: string | null;
      wireless_portal_group_names: string | null;
    }
  >
> {
  const data = await apiFetch<{
    by_imei: Record<
      string,
      {
        wireless_portal_org_name: string | null;
        wireless_portal_group_names: string | null;
      }
    >;
  }>(`/api/shipping/gosi-details/${String(mvDocumentId)}/serials/portal`);
  return data.by_imei;
}

export function filterGosiListRowsClient(rows: GosiListRow[], searchText: string): GosiListRow[] {
  const trimmed = searchText.trim().toLowerCase();
  if (trimmed === "") {
    return rows;
  }
  return rows.filter((row) => {
    const haystack = [
      String(row.mv_id),
      row.number,
      row.status,
      row.sales_order_number,
      row.customer_purchase_order_number,
      row.client_name,
      row.end_user_name,
    ]
      .filter((piece) => piece != null && String(piece).trim() !== "")
      .join(" ")
      .toLowerCase();
    return haystack.includes(trimmed);
  });
}
